/**
 * Relay client — thin wrapper over nostr-tools SimplePool.
 * Relays are dumb ciphertext transport (see CLAUDE.md).
 */
import { SimplePool, finalizeEvent, nip19, generateSecretKey, getPublicKey } from 'nostr-tools';
import type { EventTemplate, NostrEvent, Filter } from 'nostr-tools';
import { bytesToHex, hexToBytes } from '@noble/hashes/utils.js';
import { peerEncrypt, peerDecrypt } from './crypto/nip44';

export const pool = new SimplePool();

// --- identity --------------------------------------------------------------

export type Signer =
  | { type: 'nip07'; pubkey: string }
  | { type: 'local'; pubkey: string; sk: string };

declare global {
  interface Window {
    nostr?: {
      getPublicKey(): Promise<string>;
      signEvent(event: EventTemplate): Promise<NostrEvent>;
      nip44?: {
        encrypt(pubkey: string, plaintext: string): Promise<string>;
        decrypt(pubkey: string, ciphertext: string): Promise<string>;
      };
    };
  }
}

export function hasNip07(): boolean {
  return typeof window !== 'undefined' && !!window.nostr;
}

export async function nip07Signer(): Promise<Signer> {
  if (!window.nostr) throw new Error('No NIP-07 extension found');
  const pubkey = await window.nostr.getPublicKey();
  return { type: 'nip07', pubkey };
}

export function nsecSigner(nsec: string): Signer {
  const dec = nip19.decode(nsec.trim());
  if (dec.type !== 'nsec') throw new Error('Not an nsec key');
  const sk = bytesToHex(dec.data as Uint8Array);
  return { type: 'local', sk, pubkey: getPublicKey(hexToBytes(sk)) };
}

export function generateSigner(): { type: 'local'; sk: string; pubkey: string; nsec: string } {
  const skBytes = generateSecretKey();
  const sk = bytesToHex(skBytes);
  return { type: 'local', sk, pubkey: getPublicKey(skBytes), nsec: nip19.nsecEncode(skBytes) };
}

export async function sign(signer: Signer, tmpl: EventTemplate): Promise<NostrEvent> {
  if (signer.type === 'nip07') {
    if (!window.nostr) throw new Error('NIP-07 extension unavailable');
    return window.nostr.signEvent({ ...tmpl, pubkey: signer.pubkey } as EventTemplate);
  }
  return finalizeEvent(tmpl, hexToBytes(signer.sk));
}

/** Local-key material for crypto operations (key updates need ECDH).
 *  NIP-07 signers cannot peer-decrypt without nip44 support in the extension;
 *  for MVP the client requires local keys for invite redemption/key updates
 *  when the extension lacks nip44. */
export function signerSk(signer: Signer): string | null {
  return signer.type === 'local' ? signer.sk : null;
}

// --- signer crypto capability (review P0: extension users must not be second-class) ---

/**
 * Peer-encryption capability abstracted over the signer. Local keys use noble
 * directly; NIP-07 extensions work when they expose `window.nostr.nip44`
 * (Alby, Amethyst, most modern signers do). Returns null when neither is
 * available — callers must degrade gracefully (invites/keyupdates blocked,
 * posting still fine).
 */
export interface PeerCrypto {
  encryptTo(recipientPk: string, payload: unknown): Promise<string>;
  decryptFrom(senderPk: string, ct: string): Promise<unknown>;
}

export function signerCrypto(signer: Signer): PeerCrypto | null {
  if (signer.type === 'local') {
    const sk = signer.sk;
    return {
      encryptTo: async (pk, payload) => peerEncrypt(sk, pk, payload),
      decryptFrom: async (pk, ct) => peerDecrypt(sk, pk, ct),
    };
  }
  const n = window.nostr;
  if (typeof n?.nip44?.encrypt === 'function' && typeof n?.nip44?.decrypt === 'function') {
    return {
      encryptTo: (pk, payload) => n.nip44!.encrypt(pk, JSON.stringify(payload)),
      decryptFrom: async (pk, ct) => JSON.parse(await n.nip44!.decrypt(pk, ct)) as unknown,
    };
  }
  return null;
}

/** True when this signer can do peer crypto (locally or via the extension). */
export function hasCrypto(signer: Signer | null): boolean {
  if (!signer) return false;
  if (signer.type === 'local') return true;
  return !!(typeof window !== 'undefined' && typeof window.nostr?.nip44?.encrypt === 'function' && typeof window.nostr?.nip44?.decrypt === 'function');
}

// --- io --------------------------------------------------------------------

export async function publish(relays: string[], ev: NostrEvent): Promise<void> {
  await Promise.any(pool.publish(relays, ev));
}

export function subscribe(
  relays: string[],
  filters: Filter[],
  onEvent: (ev: NostrEvent) => void,
): () => void {
  // nostr-tools 2.x: one filter per subscribeMany call — fan out, combine closers.
  const closers = filters.map(f => pool.subscribeMany(relays, f, { onevent: onEvent }));
  return () => closers.forEach(c => c.close());
}

export async function query(relays: string[], filter: Filter): Promise<NostrEvent[]> {
  return pool.querySync(relays, filter);
}

export function npubOf(pk: string): string {
  return nip19.npubEncode(pk);
}
